import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable, DataTableFilterEvent, DataTablePageEvent } from 'primereact/datatable';
import { Column, ColumnEditorOptions } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Menubar } from 'primereact/menubar';
import { FilterMatchMode } from 'primereact/api';
import { ledgerApi, checkNumberProposalsApi } from '../../api';
import { LedgerEntry, CheckNumberProposal } from '../../types';
import { formatCurrency, formatDate, amountFilterFunction } from '../../utils';
import { useDataTableState, useAccountPreference, useAccountMenuItem } from '../../hooks';

interface LedgerEntriesTabProps {
  clientId: string;
}

export const LedgerEntriesTab: React.FC<LedgerEntriesTabProps> = ({ clientId }) => {
  const toast = useRef<Toast>(null);
  const tableRef = useRef<DataTable<LedgerEntry[]>>(null);
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredRowCount, setFilteredRowCount] = useState<number>(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(50);
  const { selectedAccountId } = useAccountPreference(clientId);
  const { accountMenuItem } = useAccountMenuItem(clientId, { showAllOption: true });

  // Check number proposal state
  const [proposals, setProposals] = useState<CheckNumberProposal[]>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showProposalsOnly, setShowProposalsOnly] = useState(false);
  const [editedValues, setEditedValues] = useState<Map<string, string>>(new Map());
  const [isBulkAccepting, setIsBulkAccepting] = useState(false);

  // Persistent filter and sort state
  const {
    sortField,
    sortOrder,
    handleSort,
    filters,
    handleFilter: onFilterChange,
  } = useDataTableState({
    storageKey: 'dataReview:ledgerEntries',
    defaultSort: { field: 'entry_date', order: -1 },
    defaultFilters: {
      entry_date: { value: null, matchMode: FilterMatchMode.CONTAINS },
      amount: { value: null, matchMode: 'custom_amount' as any },
      description: { value: null, matchMode: FilterMatchMode.CONTAINS },
      entry_type: { value: null, matchMode: FilterMatchMode.CONTAINS },
      payment_type: { value: null, matchMode: FilterMatchMode.CONTAINS },
      check_no: { value: null, matchMode: FilterMatchMode.CONTAINS },
    },
  });

  useEffect(() => {
    loadData();
    loadProposals();
  }, [clientId, selectedAccountId]);

  const handleFilter = (event: DataTableFilterEvent) => {
    onFilterChange(event);
    const filtered = event.filteredValue as LedgerEntry[] | null;
    setFilteredRowCount(filtered ? filtered.length : displayedEntries.length);
  };

  const onPage = (e: DataTablePageEvent) => {
    setFirst(e.first);
    setRows(e.rows);
    setTimeout(() => {
      const wrapper = tableRef.current?.getElement()?.querySelector('.p-datatable-wrapper');
      if (wrapper) {
        wrapper.scrollTop = 0;
      }
    }, 0);
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      const data = await ledgerApi.listLedgerEntries(clientId, { accountId: selectedAccountId });
      setEntries(data);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to load ledger entries',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadProposals = async () => {
    try {
      const data = await checkNumberProposalsApi.getProposals(clientId, 'pending');
      setProposals(data);
    } catch {
      setProposals([]);
    }
  };

  const handleExtract = async () => {
    setIsExtracting(true);
    try {
      const result = await checkNumberProposalsApi.extractCheckNumbers(clientId);
      toast.current?.show({
        severity: 'success',
        summary: 'Extraction Complete',
        detail: `Found ${result.proposals_created} check numbers`,
        life: 5000,
      });
      await loadProposals();
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to extract check numbers',
        life: 5000,
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const handleAccept = async (proposal: CheckNumberProposal) => {
    setProcessingId(proposal.id);
    const editedValue = editedValues.get(proposal.id);
    const finalValue = editedValue !== undefined ? editedValue : proposal.proposed_check_number;
    try {
      await checkNumberProposalsApi.acceptProposal(
        clientId,
        proposal.id,
        finalValue !== proposal.proposed_check_number ? { edited_check_number: finalValue } : undefined
      );
      setEditedValues((prev: Map<string, string>) => {
        const next = new Map(prev);
        next.delete(proposal.id);
        return next;
      });
      await Promise.all([loadData(), loadProposals()]);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to accept',
        life: 5000,
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (proposalId: string) => {
    setProcessingId(proposalId);
    try {
      await checkNumberProposalsApi.rejectProposal(clientId, proposalId);
      await loadProposals();
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to reject',
        life: 5000,
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleBulkAccept = async () => {
    if (filteredProposals.length === 0) return;
    setIsBulkAccepting(true);
    try {
      const proposalIds = filteredProposals.map((p: CheckNumberProposal) => p.id);
      const result = await checkNumberProposalsApi.bulkAccept(clientId, proposalIds);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `${result.success_count} check numbers accepted`,
        life: 3000,
      });
      setEditedValues(new Map());
      await Promise.all([loadData(), loadProposals()]);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to bulk accept',
        life: 5000,
      });
    } finally {
      setIsBulkAccepting(false);
    }
  };

  const proposalsByEntry = useMemo(() => {
    const map = new Map<string, CheckNumberProposal>();
    proposals.forEach((p) => map.set(p.ledger_entry_id, p));
    return map;
  }, [proposals]);

  const displayedEntries = useMemo(() => {
    if (!showProposalsOnly) return entries;
    return entries.filter((e: LedgerEntry) => proposalsByEntry.has(e.id));
  }, [entries, showProposalsOnly, proposalsByEntry]);

  // Filter proposals to only those matching current account's entries
  const filteredProposals = useMemo(() => {
    const entryIds = new Set(entries.map((e) => e.id));
    return proposals.filter((p) => entryIds.has(p.ledger_entry_id));
  }, [entries, proposals]);

  const menuItems = [
    accountMenuItem,
    {
      label: isExtracting ? 'Extracting...' : 'Extract Check Numbers',
      icon: isExtracting ? 'pi pi-spin pi-spinner' : 'ms-out ms-out-checkbook',
      command: handleExtract,
      disabled: isExtracting,
    },
  ];

  useEffect(() => {
    setFilteredRowCount(displayedEntries.length);
  }, [displayedEntries]);

  const handleToggleIsCustomer = async (entry: LedgerEntry) => {
    const newValue = !entry.is_customer;
    // Optimistically update the UI
    setEntries(prev => prev.map((e) => (e.id === entry.id ? { ...e, is_customer: newValue } : e)));

    try {
      await ledgerApi.toggleIsCustomer(clientId, entry.id, newValue);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Entry updated',
        life: 3000,
      });
    } catch (error: any) {
      // Revert on error
      setEntries(prev => prev.map((e) => (e.id === entry.id ? { ...e, is_customer: !newValue } : e)));
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to update entry',
        life: 5000,
      });
    }
  };

  const amountBodyTemplate = (rowData: LedgerEntry) => {
    if (rowData.amount === null || rowData.amount === undefined) {
      return '-';
    }
    const amount = Number(rowData.amount);
    // const color = amount < 0 ? 'amount-negative' : 'amount-positive';
    return formatCurrency(amount);
  };

  const isCustomerBodyTemplate = (rowData: LedgerEntry) => {
    return (
      <Checkbox
        checked={rowData.is_customer}
        onChange={() => handleToggleIsCustomer(rowData)}
      />
    );
  };

  const checkNoBodyTemplate = (rowData: LedgerEntry) => {
    if (rowData.check_no) return rowData.check_no;

    const proposal = proposalsByEntry.get(rowData.id);
    if (!proposal) return '—';

    const isProcessing = processingId === proposal.id;
    const currentValue = editedValues.has(proposal.id)
      ? editedValues.get(proposal.id)!
      : proposal.proposed_check_number;

    return (
      <div className="row-actions">
        <span className="editable-value">{currentValue}</span>
        <Button
          icon={isProcessing ? 'pi pi-spin pi-spinner' : 'ms-out ms-out-check'}
          size="small"
          link
          tooltip="Accept"
          onClick={() => handleAccept(proposal)}
          disabled={isProcessing || !currentValue}
        />
        <Button
          icon="ms-out ms-out-close"
          size="small"
          link
          tooltip="Reject"
          onClick={() => handleReject(proposal.id)}
          disabled={isProcessing}
        />
      </div>
    );
  };

  const checkNoEditor = (options: ColumnEditorOptions) => {
    const rowData = options.rowData as LedgerEntry;
    if (rowData.check_no) return rowData.check_no;

    const proposal = proposalsByEntry.get(rowData.id);
    if (!proposal) return '—';

    const currentValue = editedValues.has(proposal.id)
      ? editedValues.get(proposal.id)!
      : proposal.proposed_check_number;

    return (
      <InputText
        value={currentValue}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setEditedValues((prev: Map<string, string>) => {
            const next = new Map(prev);
            next.set(proposal.id, e.target.value);
            return next;
          });
        }}
        autoFocus
        style={{ width: '100%' }}
      />
    );
  };

  const isCellEditable = (rowData: LedgerEntry) => {
    return !rowData.check_no && proposalsByEntry.has(rowData.id);
  };

  return (
    <>
      <Toast ref={toast} />

      <Menubar
        className="actions-menubar"
        model={menuItems}
        end={
          filteredProposals.length > 0 ? (
            <div className="menu-item-actions-group warning">
              <label><i className="ms-out ms-out-warning" /> check numbers ({filteredProposals.length}): </label>
              <Button
                icon="ms-out ms-out-preview"
                label="review"
                severity="warning"
                size="small"
                outlined={!showProposalsOnly}
                onClick={() => setShowProposalsOnly(!showProposalsOnly)}
              />
              <Button
                icon={isBulkAccepting ? 'pi pi-spin pi-spinner' : 'ms-out ms-out-done-all'}
                label="accept all"
                severity="success"
                size="small"
                onClick={handleBulkAccept}
                disabled={isBulkAccepting}
              />
            </div>
          ) : undefined
        }
      />

      <DataTable
        ref={tableRef}
        value={displayedEntries}
        dataKey="id"
        loading={isLoading}
        paginator
        first={first}
        rows={rows}
        onPage={onPage}
        rowsPerPageOptions={[25, 50, 100]}
        paginatorTemplate={{
          layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
          CurrentPageReport: (options) => (
            <span className="paginator-current">
              Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> ledger entries
            </span>
          ),
        }}
        size="small"
        stripedRows
        scrollable
        scrollHeight="flex"
        filterDisplay="menu"
        filters={filters}
        onFilter={handleFilter}
        totalRecords={filteredRowCount}
        emptyMessage="No ledger entries found"
        sortField={sortField ?? undefined}
        sortOrder={sortOrder ?? undefined}
        onSort={handleSort}
        editMode="cell"
      >
        <Column
          field="entry_date"
          header="Date"
          style={{ width: '8%' }}
          body={(rowData) => formatDate(rowData.entry_date)}
          sortable
          filter
          filterPlaceholder="Search by date"
          showFilterMatchModes={false}
        />
        <Column field="amount" header="Amount" body={amountBodyTemplate} style={{ width: '10%' }} sortable filter filterPlaceholder="Search amount" showFilterMatchModes={false} filterFunction={amountFilterFunction} />
        <Column field="entry_type" header="Type" sortable style={{ width: '8%' }} filter filterPlaceholder="Search type" showFilterMatchModes={false} />
        <Column field="payment_type" header="Payment Type" sortable style={{ width: '15%' }} filter filterPlaceholder="Search payment type" showFilterMatchModes={false} />
        <Column field="description" header="Description" sortable style={{ width: '22%' }} filter filterPlaceholder="Search description" showFilterMatchModes={false} />
        <Column
          field="check_no"
          header="Check #"
          sortable
          style={{ width: '15%' }}
          filter
          filterPlaceholder="Search check #"
          showFilterMatchModes={false}
          body={checkNoBodyTemplate}
          editor={(options: ColumnEditorOptions) => isCellEditable(options.rowData as LedgerEntry) ? checkNoEditor(options) : null}
        />
        <Column
          header="Client?"
          body={isCustomerBodyTemplate}
          style={{ width: '12%', textAlign: 'center' }}
        />
      </DataTable>
    </>
  );
};
